import { useEffect, useRef, useSyncExternalStore } from 'react'
import { apiConfig, authParams, wsUrl } from './config'
import { ApiError } from './http'
import { session } from './session'

export type RpcStatus = 'idle' | 'connecting' | 'open' | 'offline' | 'failed'
type EventHandler = (payload: any) => void

interface Pending { method: string; params: unknown; resolve(v: unknown): void; reject(e: Error): void }

/**
 * One WebSocket per tab. Calls made while (re)connecting are queued; after a
 * reconnect, "rpc.reconnected" fires so views can re-subscribe and refetch.
 */
class RpcClient {
  private ws: WebSocket | null = null
  private status: RpcStatus = 'idle'
  private statusListeners = new Set<() => void>()
  private handlers = new Map<string, Set<EventHandler>>()
  private waiting = new Map<string, Pending>()
  private queue: { id: string; p: Pending }[] = []
  private nextId = 1
  private backoff = 1000
  private ready = false
  private stopped = true
  private everOpened = false
  private failures = 0

  start(): void {
    if (!this.stopped) return
    this.stopped = false
    this.open()
  }

  stop(): void {
    this.stopped = true
    this.ws?.close()
    this.ws = null
    this.setStatus('idle')
  }

  getStatus = (): RpcStatus => this.status
  subscribeStatus = (l: () => void): (() => void) => {
    this.statusListeners.add(l)
    return () => this.statusListeners.delete(l)
  }

  on(event: string, h: EventHandler): () => void {
    let set = this.handlers.get(event)
    if (!set) this.handlers.set(event, (set = new Set()))
    set.add(h)
    return () => set!.delete(h)
  }

  call<T = unknown>(method: string, params: unknown = {}): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = String(this.nextId++)
      const p: Pending = { method, params, resolve: resolve as (v: unknown) => void, reject }
      if (this.ready && this.ws?.readyState === WebSocket.OPEN) this.send(id, p)
      else this.queue.push({ id, p })
    })
  }

  /** After the access token is refreshed, re-authenticate the open socket. */
  reauth(): void {
    const token = session.get().token
    if (this.ready && token) void this.raw('auth', { token }).catch(() => undefined)
  }

  private setStatus(s: RpcStatus): void {
    this.status = s
    for (const l of this.statusListeners) l()
  }

  private emit(event: string, payload: unknown): void {
    for (const h of this.handlers.get(event) ?? []) h(payload)
  }

  private send(id: string, p: Pending): void {
    this.waiting.set(id, p)
    this.ws!.send(JSON.stringify({ id, method: p.method, params: p.params }))
  }

  private raw(method: string, params: unknown): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const id = String(this.nextId++)
      this.send(id, { method, params, resolve, reject })
    })
  }

  private open(): void {
    const token = session.get().token
    if (!token) return
    this.setStatus('connecting')
    const ws = new WebSocket(wsUrl(apiConfig().baseUrl, location))
    this.ws = ws
    ws.onopen = async () => {
      try {
        await this.raw('auth', authParams(session.get().token!, apiConfig().client))
      } catch {
        if (apiConfig().canRefresh && (await session.refresh())) return ws.close()
        session.expired()
        return
      }
      this.ready = true
      this.backoff = 1000
      this.failures = 0
      this.setStatus('open')
      for (const { id, p } of this.queue.splice(0)) this.send(id, p)
      if (this.everOpened) this.emit('rpc.reconnected', null)
      this.everOpened = true
    }
    ws.onmessage = ev => {
      const msg = JSON.parse(ev.data as string)
      if (msg.event) {
        if (msg.event === 'session.revoked') session.expired()
        this.emit(msg.event, msg.payload)
        return
      }
      const p = this.waiting.get(String(msg.id))
      if (!p) return
      this.waiting.delete(String(msg.id))
      if (msg.error) p.reject(new ApiError(0, msg.error.code, msg.error.message, msg.error.details))
      else p.resolve(msg.result)
    }
    ws.onclose = ev => {
      this.ready = false
      // Requests already sent may or may not have run (e.g. chats.send): never replay them.
      for (const [id, p] of this.waiting) {
        this.waiting.delete(id)
        p.reject(new ApiError(0, 'OFFLINE', 'Connection lost'))
      }
      if (this.stopped) return
      if (ev.code === 4401) {
        // The server revoked this session. A token client has nothing to refresh: sign out.
        if (!apiConfig().canRefresh) return session.expired()
        void session.refresh().then(ok => { if (ok) this.open() })
        return
      }
      // A refused upgrade (403: this origin is not in EXTENSION_IDS) reaches us as a generic close,
      // so the panel is told "failed" rather than "offline" once retrying has clearly not helped.
      this.failures++
      this.setStatus(this.failures >= 3 ? 'failed' : 'offline')
      setTimeout(() => { if (!this.stopped) this.open() }, this.backoff)
      this.backoff = Math.min(this.backoff * 2, 30_000)
    }
  }
}

export const rpc = new RpcClient()

export function useRpcStatus(): RpcStatus {
  return useSyncExternalStore(rpc.subscribeStatus, rpc.getStatus)
}

/** Subscribe to a server event for the component's lifetime (latest handler wins). */
export function useRpcEvent(event: string, handler: EventHandler): void {
  const ref = useRef(handler)
  ref.current = handler
  useEffect(() => rpc.on(event, p => ref.current(p)), [event])
}
